// File: src/components/lms/ProgressBar/ProgressBar.tsx

import React from "react";
import styled, { css, keyframes } from "styled-components";
import { DefaultTheme } from "styled-components";

interface ProgressBarProps {
  progress: number; // 0 to 100
  height?: keyof DefaultTheme["spacing"];
  color?: keyof DefaultTheme["colors"]; // "primary" | "success" | etc.
  gradient?: keyof DefaultTheme["gradients"]; // e.g. "primary", "success", "radialPrimary"
  showLabel?: boolean;
  animated?: boolean;
}

// ✅ Keyframe animation for mount-in effect
const animateFill = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

const BarWrapper = styled.div<{ height: keyof DefaultTheme["spacing"] }>`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.divider};
  border-radius: ${({ theme }) => theme.radii.rounded};
  overflow: hidden;
  height: ${({ height, theme }) => theme.spacing[height]};
`;

const BarFill = styled.div<{
  progress: number;
  color?: keyof DefaultTheme["colors"];
  gradient?: keyof DefaultTheme["gradients"];
  animated?: boolean;
}>`
  height: 100%;
  ${({ theme, color, gradient }) =>
    gradient
      ? css`
          background: ${theme.gradients[gradient]};
        `
      : css`
          background-color: ${theme.colors[color || "primary"]};
        `}
  ${({ progress }) => css`
    width: ${progress}%;
  `}
  ${({ animated }) =>
    animated &&
    css`
      animation: ${animateFill} 1s ease forwards;
    `}
  transition: width 0.3s ease;
`;

const Label = styled.div`
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  font-weight: ${({ theme }) => theme.typography.caption.fontWeight};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: right;
  margin-top: ${({ theme }) => theme.spacing.xs};
  line-height: ${({ theme }) => theme.typography.caption.lineHeight};
`;

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  height = "sm",
  color = "primary",
  gradient,
  showLabel = false,
  animated = false,
}) => {
  return (
    <div>
      <BarWrapper height={height}>
        <BarFill
          progress={progress}
          color={color}
          gradient={gradient}
          animated={animated}
        />
      </BarWrapper>
      {showLabel && <Label>{progress}% complete</Label>}
    </div>
  );
};